import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import NotificationToast from '../../../components/ui/NotificationToast';
import jobApplicationService from '../../../services/jobApplicationService';
import crmService from '../../../services/crmService';
import apiClient from '../../../lib/apiClient';

const ImportApplicantsModal = ({ isOpen, onClose, onImportSuccess }) => {
  const [importType, setImportType] = useState('leads'); // 'leads' or 'excel'
  const [validatedLeads, setValidatedLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [file, setFile] = useState(null);
  const [defaultPosition, setDefaultPosition] = useState('');
  const [defaultDepartment, setDefaultDepartment] = useState('');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isOpen && importType === 'leads') {
      loadValidatedLeads();
    }
  }, [isOpen, importType]);

  const loadValidatedLeads = async () => {
    try {
      setLoadingLeads(true);
      const result = await jobApplicationService.getValidatedLeads();
      if (result.success && result.data) {
        setValidatedLeads(result.data);
      } else {
        setToast({ type: 'error', message: result.error || 'Failed to load validated leads' });
      }
    } catch (error) {
      setToast({ type: 'error', message: error.message || 'Failed to load validated leads' });
    } finally {
      setLoadingLeads(false);
    }
  };

  const handleLeadToggle = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLeads.length === validatedLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(validatedLeads.map(lead => lead.id));
    }
  };

  const handleImportFromLeads = async () => {
    if (selectedLeads.length === 0) {
      setToast({ type: 'error', message: 'Please select at least one lead to import' });
      return;
    }

    try {
      setLoading(true);
      const result = await jobApplicationService.importFromLeads({
        lead_ids: selectedLeads,
        default_position: defaultPosition || undefined,
        default_department: defaultDepartment || undefined
      });

      if (result.success) {
        const { successful, failed, total } = result.data;
        setToast({ 
          type: 'success', 
          message: `Successfully imported ${successful} of ${total} applicants${failed > 0 ? `. ${failed} failed.` : ''}` 
        });
        
        if (onImportSuccess) {
          onImportSuccess();
        }
        
        setTimeout(() => {
          onClose();
          setSelectedLeads([]);
          setDefaultPosition('');
          setDefaultDepartment('');
        }, 1500);
      } else {
        setToast({ type: 'error', message: result.error || 'Failed to import applicants' });
      }
    } catch (error) {
      setToast({ type: 'error', message: error.message || 'Failed to import applicants' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
        'application/csv'
      ];
      const allowedExtensions = ['xlsx', 'xls', 'csv'];
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(fileExtension)) {
        setToast({ type: 'error', message: 'Please upload a valid Excel (.xlsx, .xls) or CSV file' });
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
    }
  };

  const handleImportFromExcel = async () => {
    if (!file) {
      setToast({ type: 'error', message: 'Please select an Excel file to import' });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', file);

      const result = await jobApplicationService.importFromExcel(formData);

      if (result.success) {
        const { successful, failed, total } = result.data;
        setToast({ 
          type: 'success', 
          message: `Successfully imported ${successful} of ${total} applicants${failed > 0 ? `. ${failed} failed.` : ''}` 
        });
        
        if (onImportSuccess) {
          onImportSuccess();
        }
        
        setTimeout(() => {
          onClose();
          setFile(null);
        }, 1500);
      } else {
        setToast({ type: 'error', message: result.error || 'Failed to import applicants' });
      }
    } catch (error) {
      setToast({ type: 'error', message: error.message || 'Failed to import applicants' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = apiClient.getToken();
      if (!token) {
        setToast({ type: 'error', message: 'Authentication required. Please log in again.' });
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || import.meta.env.VITE_API_BASE_URL || '/api';
      const response = await fetch(`${apiUrl}/job-applications/download-template`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Determine filename from Content-Type or Content-Disposition header
        const contentType = response.headers.get('Content-Type') || '';
        const contentDisposition = response.headers.get('Content-Disposition') || '';
        
        let filename = 'applicant_import_template.csv'; // Default to CSV
        if (contentType.includes('spreadsheetml') || contentType.includes('excel')) {
          filename = 'applicant_import_template.xlsx';
        } else if (contentDisposition.includes('filename=')) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match) {
            filename = match[1];
          }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        const fileType = filename.endsWith('.csv') ? 'CSV' : 'Excel';
        setToast({ type: 'success', message: `${fileType} template downloaded successfully` });
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Failed to download template' }));
        setToast({ type: 'error', message: errorData.error || 'Failed to download template' });
      }
    } catch (error) {
      setToast({ type: 'error', message: error.message || 'Failed to download template' });
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-card rounded-lg border border-border shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div>
              <h2 className="text-xl font-semibold text-foreground">Import Applicants</h2>
              <p className="text-sm text-muted-foreground mt-1">Choose an import method</p>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Import Type Selection */}
            <div className="mb-6">
              <div className="flex gap-4">
                <button
                  onClick={() => setImportType('leads')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    importType === 'leads'
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon name="Users" size={24} className={importType === 'leads' ? 'text-primary' : 'text-muted-foreground'} />
                    <div className="text-left">
                      <div className="font-medium text-foreground">Import from Validated Leads</div>
                      <div className="text-sm text-muted-foreground">Import from CRM Lead Management</div>
                    </div>
                  </div>
                </button>
                <button
                  onClick={() => setImportType('excel')}
                  className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                    importType === 'excel'
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon name="FileSpreadsheet" size={24} className={importType === 'excel' ? 'text-primary' : 'text-muted-foreground'} />
                    <div className="text-left">
                      <div className="font-medium text-foreground">Import from Excel Template</div>
                      <div className="text-sm text-muted-foreground">Upload Excel or CSV file</div>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Import from Leads */}
            {importType === 'leads' && (
              <div className="space-y-4">
                {/* Default Values */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Default Position (Optional)
                    </label>
                    <Input
                      value={defaultPosition}
                      onChange={(e) => setDefaultPosition(e.target.value)}
                      placeholder="e.g., Software Developer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Default Department (Optional)
                    </label>
                    <Input
                      value={defaultDepartment}
                      onChange={(e) => setDefaultDepartment(e.target.value)}
                      placeholder="e.g., IT"
                    />
                  </div>
                </div>

                {/* Leads List */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-foreground">
                      Select Validated Leads ({selectedLeads.length} selected)
                    </label>
                    {validatedLeads.length > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAll}
                      >
                        {selectedLeads.length === validatedLeads.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    )}
                  </div>

                  {loadingLeads ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : validatedLeads.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Icon name="Inbox" size={48} className="mx-auto mb-2 opacity-50" />
                      <p>No validated leads found</p>
                      <p className="text-sm mt-1">Validate leads in CRM Lead Management first</p>
                    </div>
                  ) : (
                    <div className="border border-border rounded-lg max-h-96 overflow-y-auto">
                      <table className="w-full">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="p-3 text-left">
                              <input
                                type="checkbox"
                                checked={selectedLeads.length === validatedLeads.length && validatedLeads.length > 0}
                                onChange={handleSelectAll}
                                className="rounded"
                              />
                            </th>
                            <th className="p-3 text-left text-sm font-medium text-foreground">Name</th>
                            <th className="p-3 text-left text-sm font-medium text-foreground">Email</th>
                            <th className="p-3 text-left text-sm font-medium text-foreground">Phone</th>
                            <th className="p-3 text-left text-sm font-medium text-foreground">Position</th>
                            <th className="p-3 text-left text-sm font-medium text-foreground">Location</th>
                          </tr>
                        </thead>
                        <tbody>
                          {validatedLeads.map((lead) => (
                            <tr
                              key={lead.id}
                              className={`border-t border-border hover:bg-muted/30 ${
                                selectedLeads.includes(lead.id) ? 'bg-primary/5' : ''
                              }`}
                            >
                              <td className="p-3">
                                <input
                                  type="checkbox"
                                  checked={selectedLeads.includes(lead.id)}
                                  onChange={() => handleLeadToggle(lead.id)}
                                  className="rounded"
                                />
                              </td>
                              <td className="p-3 text-sm text-foreground">{lead.name}</td>
                              <td className="p-3 text-sm text-foreground">{lead.email}</td>
                              <td className="p-3 text-sm text-foreground">{lead.phone || '-'}</td>
                              <td className="p-3 text-sm text-foreground">{lead.position || '-'}</td>
                              <td className="p-3 text-sm text-foreground">{lead.location || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Import from Excel */}
            {importType === 'excel' && (
              <div className="space-y-4">
                <div className="bg-muted/30 border border-border rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <Icon name="Info" size={24} className="text-primary mt-1" />
                    <div className="flex-1">
                      <h3 className="font-medium text-foreground mb-2">Excel/CSV Template Format</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Your Excel (.xlsx, .xls) or CSV file should include the following columns:
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                        <li>• <strong>Applicant Name</strong> (Required)</li>
                        <li>• <strong>Email</strong> (Required)</li>
                        <li>• Phone</li>
                        <li>• Position</li>
                        <li>• Department</li>
                        <li>• Experience Level</li>
                        <li>• Expected Salary</li>
                        <li>• Location</li>
                      </ul>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadTemplate}
                        iconName="Download"
                        iconPosition="left"
                      >
                        Download Template
                      </Button>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Select Excel File (.xlsx, .xls, or .csv)
                  </label>
                  <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                    <input
                      type="file"
                      id="excel-file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    <label htmlFor="excel-file" className="cursor-pointer">
                      {file ? (
                        <div className="space-y-2">
                          <Icon name="FileCheck" size={48} className="mx-auto text-primary" />
                          <p className="text-foreground font-medium">{file.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {(file.size / 1024).toFixed(2)} KB
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.preventDefault();
                              setFile(null);
                              document.getElementById('excel-file').value = '';
                            }}
                          >
                            Change File
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Icon name="Upload" size={48} className="mx-auto text-muted-foreground" />
                          <p className="text-foreground font-medium">Click to upload or drag and drop</p>
                          <p className="text-sm text-muted-foreground">
                            Excel (.xlsx, .xls) or CSV files only
                          </p>
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={importType === 'leads' ? handleImportFromLeads : handleImportFromExcel}
              disabled={loading || (importType === 'leads' && selectedLeads.length === 0) || (importType === 'excel' && !file)}
              iconName="Upload"
              iconPosition="left"
            >
              {loading ? 'Importing...' : 'Import Applicants'}
            </Button>
          </div>
        </div>
      </div>

      {toast && (
        <NotificationToast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default ImportApplicantsModal;

